import { useCallback, useState } from 'react'

import { externalAuthApi } from '../api'
import { BASE_PATH_B2B, ROUTES } from '../constants'
import { ERROR_MESSAGES } from '../utils/errorMessages'
import { type UseAuthBaseOptions, useAuthBase } from './useAuthBase'

export type UseSignupOtpOptions = {
  email: string
  basePath?: string
} & UseAuthBaseOptions

interface UseSignupOtpReturn {
  isLoading: boolean
  error: string | undefined
  setError: (error: string | undefined) => void
  validateCode: (code: string) => Promise<void>
  resendCode: () => Promise<boolean>
}

function buildDetailsUrl(
  token: string,
  { basePath, nextUrl }: { basePath?: string; nextUrl?: string }
): string {
  const detailsPath =
    basePath === BASE_PATH_B2B ? ROUTES.B2B.SIGN_UP.DETAILS : ROUTES.SIGN_UP.DETAILS
  // `token` is already URL-encoded by Bifrost's validate-otp (same as the
  // magic-link email URL), so it goes into the path as-is. Re-encoding here
  // would double-encode it and break the later /cadastro/cpf token handoff.
  const url = `${detailsPath}/${token}`

  // Preserve EVERY query param of the /login route (same behavior as the magic-link
  // navigation, which spreads router.query). This works for both the standalone page
  // and the iframe: in the iframe the trigger params live on its own /login URL and
  // step navigation is in-memory, so window.location.search still holds them here.
  const params =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search)
  // The iframe's next_url comes from Bifrost.open() at runtime (not on the URL), so
  // apply it as an explicit override when provided.
  if (nextUrl) params.set('next_url', nextUrl)

  const query = params.toString()
  return query ? `${url}?${query}` : url
}

/**
 * Drives the OTP confirmation step of the signup flow: validates the typed code
 * and resends a new one. On a valid code, Bifrost returns a signup token (same
 * format consumed by `/cadastro/email/:token`) and we hand off to the existing
 * complete-data page — the account is only created there.
 */
export function useSignupOtp(options: UseSignupOtpOptions): UseSignupOtpReturn {
  const { email, basePath, nextUrl } = options
  const { error, setError, redirectToCallback, handleAsyncOperation } = useAuthBase(options)
  const [isLoading, setIsLoading] = useState(false)

  const validateCode = useCallback(
    async (code: string) => {
      setIsLoading(true)
      setError(undefined)
      const result = await handleAsyncOperation(async () =>
        externalAuthApi.validateSignupOtp({ email, otp: code })
      )

      if (!result) {
        setIsLoading(false)
        return
      }

      const { response, data } = result

      if (response.ok && data?.token) {
        // Keep the loading state through the full-page navigation.
        redirectToCallback(buildDetailsUrl(data.token, { basePath, nextUrl }))
        return
      }

      setError(data?.message || ERROR_MESSAGES.signup.otp.server.invalid)
      setIsLoading(false)
    },
    [email, basePath, nextUrl, handleAsyncOperation, redirectToCallback, setError]
  )

  const resendCode = useCallback(async (): Promise<boolean> => {
    const result = await handleAsyncOperation(async () => externalAuthApi.sendSignupOtp({ email }))
    return !!result?.response.ok
  }, [email, handleAsyncOperation])

  return {
    isLoading,
    error,
    setError,
    validateCode,
    resendCode,
  }
}
